import { cbc } from '@noble/ciphers/aes.js';
import { hmac } from '@noble/hashes/hmac.js';
import { sha256 } from '@noble/hashes/sha2.js';
import { Unzip, UnzipInflate, UnzipPassThrough, Zip, ZipDeflate, ZipPassThrough } from 'fflate';
import { normalizeRelativePath } from './filesystem';
import type { MsiInspection } from './msi';
import type { BuildProgress, CompressionLevel, PackageInspection, PackageResult, SourceFile } from './types';
import { getToolVersion } from './tool-version';

type SyncAccess = { write(data: ArrayBufferView, options?: { at?: number }): number; read(data: ArrayBufferView, options?: { at?: number }): number; getSize(): number; flush(): void; close(): void };

const OPFS_JOB_NAME = /^\.intune-[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\.(?:encrypted|package)$/i;
const DEFAULT_STALE_JOB_AGE_MS = 24 * 60 * 60 * 1000;

/** True only for temp names created by this application's streaming pipeline. */
export function isOwnedOpfsJobName(name: string): boolean {
  return OPFS_JOB_NAME.test(name);
}

/**
 * Remove abandoned streaming jobs left by a crashed tab or worker.
 *
 * The age check prevents removing a job that another tab may currently be
 * using, and the strict filename check keeps cleanup scoped to our own files.
 */
export async function cleanupStaleOpfsJobs(maxAgeMs = DEFAULT_STALE_JOB_AGE_MS): Promise<number> {
  if (!navigator.storage?.getDirectory) return 0;
  const root = await navigator.storage.getDirectory();
  const cutoff = Date.now() - Math.max(0, maxAgeMs);
  let removed = 0;
  for await (const entry of root.values()) {
    if (entry.kind !== 'file' || !isOwnedOpfsJobName(entry.name)) continue;
    try {
      const file = await (entry as FileSystemFileHandle).getFile();
      if (Number.isFinite(file.lastModified) && file.lastModified > cutoff) continue;
      await root.removeEntry(entry.name);
      removed += 1;
    } catch {
      // A concurrent build or revoked permission should not prevent the app
      // from loading; leave that entry for a later cleanup attempt.
    }
  }
  return removed;
}

const encoder = new TextEncoder();
const concat = (...parts: Uint8Array[]) => { const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0)); let at = 0; for (const part of parts) { out.set(part, at); at += part.length; } return out; };
const b64 = (bytes: Uint8Array) => { let text = ''; for (let i = 0; i < bytes.length; i += 0x8000) text += String.fromCharCode(...bytes.subarray(i, i + 0x8000)); return btoa(text); };
const xml = (value: string) => value.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;').replaceAll('"', '&quot;').replaceAll("'", '&apos;');
const random = (length: number) => { const bytes = new Uint8Array(length); crypto.getRandomValues(bytes); return bytes; };
const ZIP_EPOCH = new Date(1980, 0, 1, 12, 0, 0);
const CONTENT_ENTRY = 'IntuneWinPackage/Contents/IntunePackage.intunewin';
const METADATA_ENTRY = 'IntuneWinPackage/Metadata/Detection.xml';
function zipDate(lastModified: number): Date {
  const date = new Date(lastModified || ZIP_EPOCH.getTime());
  if (!Number.isFinite(date.getTime()) || date.getFullYear() < 1980) return ZIP_EPOCH;
  if (date.getFullYear() > 2099) return new Date(2099, 11, 31, 12, 0, 0);
  return date;
}

function msiInfoXml(info?: MsiInspection): string {
  if (!info?.parser) return '';
  const bool = (value?: boolean) => String(value === true);
  const context = info.installContext === 'Per-machine' ? 'System' : info.installContext === 'Per-user' ? 'User' : 'Any';
  return `<MsiInfo><MsiProductCode>${xml(info.productCode || '')}</MsiProductCode><MsiProductVersion>${xml(info.productVersion || '')}</MsiProductVersion><MsiPackageCode>${xml(info.packageCode || '')}</MsiPackageCode><MsiUpgradeCode>${xml(info.upgradeCode || '')}</MsiUpgradeCode><MsiExecutionContext>${context}</MsiExecutionContext><MsiRequiresLogon>${bool(info.requiresLogon)}</MsiRequiresLogon><MsiRequiresReboot>${bool(info.requiresReboot)}</MsiRequiresReboot><MsiIsMachineInstall>${bool(info.isMachineInstall)}</MsiIsMachineInstall><MsiIsUserInstall>${bool(info.isUserInstall)}</MsiIsUserInstall><MsiIncludesServices>${bool(info.includesServices)}</MsiIncludesServices><MsiIncludesODBCDataSource>${bool(info.includesODBCDataSource)}</MsiIncludesODBCDataSource><MsiContainsSystemRegistryKeys>${bool(info.containsSystemRegistryKeys)}</MsiContainsSystemRegistryKeys><MsiContainsSystemFolders>${bool(info.containsSystemFolders)}</MsiContainsSystemFolders><MsiPublisher>${xml(info.manufacturer || '')}</MsiPublisher></MsiInfo>`;
}

function detectionXml(outputName: string, setupFile: string, size: number, key: Uint8Array, macKey: Uint8Array, iv: Uint8Array, mac: Uint8Array, digest: Uint8Array, msiInfo?: MsiInspection, useMicrosoftCompatibleMetadata = false) {
  return encoder.encode(`<ApplicationInfo xmlns:xsd="http://www.w3.org/2001/XMLSchema" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" ToolVersion="${getToolVersion(useMicrosoftCompatibleMetadata)}"><Name>${xml(outputName.replace(/\.intunewin$/i, ''))}</Name><Description></Description><UnencryptedContentSize>${size}</UnencryptedContentSize><FileName>IntunePackage.intunewin</FileName><SetupFile>${xml(setupFile)}</SetupFile><EncryptionInfo><EncryptionKey>${b64(key)}</EncryptionKey><MacKey>${b64(macKey)}</MacKey><InitializationVector>${b64(iv)}</InitializationVector><Mac>${b64(mac)}</Mac><ProfileIdentifier>ProfileVersion1</ProfileIdentifier><FileDigest>${b64(digest)}</FileDigest><FileDigestAlgorithm>SHA256</FileDigestAlgorithm></EncryptionInfo>${msiInfoXml(msiInfo)}</ApplicationInfo>`);
}

const decoder = new TextDecoder();
const xmlUnescape = (value: string) => value.replaceAll('&lt;', '<').replaceAll('&gt;', '>').replaceAll('&quot;', '"').replaceAll('&apos;', "'").replaceAll('&amp;', '&');
const readTag = (text: string, tag: string) => { const value = text.match(new RegExp(`<${tag}>([^<]*)</${tag}>`))?.[1]; return value === undefined ? undefined : xmlUnescape(value); };
const decode64 = (value: string) => Uint8Array.from(atob(value), (char) => char.charCodeAt(0));
const equalBytes = (a: Uint8Array, b: Uint8Array) => a.length === b.length && a.every((value, index) => value === b[index]);

async function streamOuterEntry(file: File, target: string, onChunk: (chunk: Uint8Array) => void): Promise<void> {
  let found = false;
  let ended = false;
  let failure: Error | null = null;
  let resolveEntry!: () => void;
  let rejectEntry!: (error: Error) => void;
  const entryDone = new Promise<void>((resolve, reject) => { resolveEntry = resolve; rejectEntry = reject; });
  const unzip = new Unzip((entry) => {
    if (entry.name !== target) return;
    found = true;
    entry.ondata = (error, chunk, final) => {
      if (error) { failure = new Error(String(error)); rejectEntry(failure); return; }
      if (chunk?.length) onChunk(new Uint8Array(chunk));
      if (final) { ended = true; resolveEntry(); }
    };
    try { entry.start(); } catch (error) { failure = error instanceof Error ? error : new Error(String(error)); rejectEntry(failure); }
  });
  unzip.register(UnzipPassThrough);
  unzip.register(UnzipInflate);
  const reader = file.stream().getReader();
  try {
    while (true) {
      const next = await reader.read();
      if (next.done) break;
      unzip.push(new Uint8Array(next.value), false);
    }
    unzip.push(new Uint8Array(0), true);
  } finally { reader.releaseLock(); }
  if (!found) throw new Error(`The outer ZIP is missing ${target}.`);
  if (!ended && !failure) await entryDone;
  if (failure) throw failure;
}

async function readOuterEntry(file: File, target: string, maxBytes = 2 * 1024 * 1024): Promise<Uint8Array> {
  const chunks: Uint8Array[] = [];
  let total = 0;
  await streamOuterEntry(file, target, (chunk) => {
    total += chunk.length;
    if (total > maxBytes) throw new Error(`${target} exceeds the verification metadata limit.`);
    chunks.push(chunk.slice());
  });
  const result = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) { result.set(chunk, offset); offset += chunk.length; }
  return result;
}

/** Verify the OPFS-backed outer archive without buffering its encrypted entry. */
export async function verifyStreamingPackage(file: File, setupFile: string): Promise<{ ok: true } | { ok: false; error: string }> {
  try {
    const metadata = decoder.decode(await readOuterEntry(file, METADATA_ENTRY));
    const keyText = readTag(metadata, 'EncryptionKey');
    const macKeyText = readTag(metadata, 'MacKey');
    const ivText = readTag(metadata, 'InitializationVector');
    const expectedMacText = readTag(metadata, 'Mac');
    const digestText = readTag(metadata, 'FileDigest');
    const declaredSize = Number(readTag(metadata, 'UnencryptedContentSize'));
    const declaredSetup = readTag(metadata, 'SetupFile');
    if (!keyText || !macKeyText || !ivText || !expectedMacText || !digestText || !declaredSetup || !Number.isSafeInteger(declaredSize) || declaredSize < 0) return { ok: false, error: 'Detection.xml is missing required encryption metadata.' };
    if (normalizeRelativePath(declaredSetup) !== normalizeRelativePath(setupFile)) return { ok: false, error: 'Detection.xml setup path does not match the selected setup file.' };
    const key = decode64(keyText); const macKey = decode64(macKeyText); const expectedIv = decode64(ivText); const expectedMac = decode64(expectedMacText); const digest = decode64(digestText);
    if (key.length !== 32 || macKey.length !== 32 || expectedIv.length !== 16 || expectedMac.length !== 32 || digest.length !== 32) return { ok: false, error: 'Detection.xml contains invalid encryption metadata lengths.' };
    const expectedCiphertextLength = declaredSize + (16 - (declaredSize % 16));
    let total = 0; let headerOffset = 0; const header = new Uint8Array(48); const macState: { value: ReturnType<typeof hmac.create> | null } = { value: null };
    await streamOuterEntry(file, CONTENT_ENTRY, (chunk) => {
      total += chunk.length;
      let offset = 0;
      if (headerOffset < header.length) {
        const take = Math.min(header.length - headerOffset, chunk.length);
        header.set(chunk.subarray(0, take), headerOffset); headerOffset += take; offset = take;
        if (headerOffset === header.length) {
          if (!equalBytes(header.subarray(32, 48), expectedIv)) throw new Error('The encrypted package IV does not match Detection.xml.');
          macState.value = hmac.create(sha256, macKey); macState.value.update(expectedIv);
        }
      }
      if (offset < chunk.length) macState.value?.update(chunk.subarray(offset));
    });
    if (headerOffset !== 48 || total !== 48 + expectedCiphertextLength) return { ok: false, error: 'The encrypted package length does not match Detection.xml.' };
    const computedMac = macState.value?.digest();
    if (!computedMac || !equalBytes(header.subarray(0, 32), computedMac) || !equalBytes(header.subarray(0, 32), expectedMac)) return { ok: false, error: 'The encrypted package MAC is invalid.' };
    // The plaintext digest is authenticated by the HMAC and is checked by
    // the full verifier; this pass intentionally avoids a second decryption.
    return { ok: true };
  } catch (error) { return { ok: false, error: error instanceof Error ? error.message : 'Streaming package verification failed.' }; }
}

/** Inspect and verify an existing .intunewin without buffering its payload. */
export async function inspectPackageFile(file: File): Promise<PackageInspection> {
  const metadata = decoder.decode(await readOuterEntry(file, METADATA_ENTRY));
  const application = metadata.match(/<ApplicationInfo\b[^>]*\bToolVersion="([^"]*)"[^>]*>/)?.[1] || '';
  const inspection: PackageInspection = {
    fileName: file.name,
    packageSize: file.size,
    toolVersion: xmlUnescape(application),
    name: readTag(metadata, 'Name') || '',
    setupFile: readTag(metadata, 'SetupFile') || '',
    unencryptedContentSize: Number(readTag(metadata, 'UnencryptedContentSize') || NaN),
    fileDigest: readTag(metadata, 'FileDigest') || '',
    profileIdentifier: readTag(metadata, 'ProfileIdentifier') || '',
    hasMsiInfo: /<MsiInfo\b/i.test(metadata),
    verification: { ok: false },
    warnings: [],
  };
  if (!inspection.setupFile) inspection.verification = { ok: false, error: 'Detection.xml does not declare a setup file.' };
  else inspection.verification = await verifyStreamingPackage(file, inspection.setupFile);
  if (!/^\d+(?:\.\d+){1,3}$/.test(inspection.toolVersion)) inspection.warnings.push('ToolVersion is not numeric; Intune may show an old Content Prep Tool warning.');
  if (inspection.profileIdentifier !== 'ProfileVersion1') inspection.warnings.push('The package uses an unknown encryption profile.');
  if (!inspection.hasMsiInfo && /\.msi$/i.test(inspection.setupFile)) inspection.warnings.push('The setup file is an MSI but Detection.xml has no MsiInfo block.');
  if (!inspection.verification.ok && inspection.verification.error) inspection.warnings.push(`Verification failed: ${inspection.verification.error}`);
  return inspection;
}

/**
 * Worker-only OPFS pipeline. ZIP bytes, ciphertext, and the final outer
 * archive are written in chunks to synchronous OPFS handles. The returned
 * path is read by the main thread only while saving to the user's destination.
 */
export async function createPackageStreaming(files: SourceFile[], setupFile: string, outputName: string, onProgress: (update: BuildProgress) => void, signal?: AbortSignal, compressionLevel: CompressionLevel = 6, msiInfo?: MsiInspection, useMicrosoftCompatibleMetadata = false): Promise<PackageResult> {
  if (!navigator.storage?.getDirectory) throw new Error('OPFS worker handles are unavailable.');
  const root = await navigator.storage.getDirectory();
  const id = crypto.randomUUID();
  const encryptedName = `.intune-${id}.encrypted`;
  const outputPath = `.intune-${id}.package`;
  const encryptedHandle = await root.getFileHandle(encryptedName, { create: true });
  const encryptedAccess = await (encryptedHandle as FileSystemFileHandle & { createSyncAccessHandle: () => Promise<SyncAccess> }).createSyncAccessHandle();
  const key = random(32); const macKey = random(32); const iv = random(16);
  let encryptedOffset = 48;
  encryptedAccess.write(new Uint8Array(32), { at: 0 }); encryptedAccess.write(iv, { at: 32 });
  const macState = hmac.create(sha256, macKey); macState.update(iv);
  const digestState = sha256.create();
  let currentIv = iv; let carry = new Uint8Array(0); let innerSize = 0; let finalized = false;
  const writeCipher = (plain: Uint8Array) => {
    if (!plain.length) return;
    const encrypted = cbc(key, currentIv, { disablePadding: true }).encrypt(plain);
    encryptedAccess.write(encrypted, { at: encryptedOffset }); encryptedOffset += encrypted.length; macState.update(encrypted); currentIv = encrypted.slice(-16);
  };
  const consumePlain = (chunk: Uint8Array, final = false) => {
    if (chunk.length) { digestState.update(chunk); innerSize += chunk.length; }
    const data = carry.length ? concat(carry, chunk) : chunk;
    const aligned = data.length - (data.length % 16);
    if (aligned) { writeCipher(data.slice(0, aligned)); carry = data.slice(aligned) as Uint8Array<ArrayBuffer>; } else carry = data as Uint8Array<ArrayBuffer>;
    if (final) { const padding = (16 - (innerSize % 16)) || 16; writeCipher(concat(carry, new Uint8Array(padding).fill(padding))); carry = new Uint8Array(0); finalized = true; }
  };
  let zipFailure: Error | null = null;
  const zip = new Zip((error, chunk, final) => {
    if (error) { zipFailure = new Error(String(error)); return; }
    if (chunk?.length) consumePlain(new Uint8Array(chunk));
    if (final && !finalized) consumePlain(new Uint8Array(0), true);
  });
  try {
    for (let index = 0; index < files.length; index += 1) {
      if (signal?.aborted) throw new DOMException('Canceled', 'AbortError');
      const entry = files[index]; const file = await entry.getFile();
      const stream = compressionLevel === 0
        ? new ZipPassThrough(normalizeRelativePath(entry.path))
        : new ZipDeflate(normalizeRelativePath(entry.path), { level: compressionLevel });
      stream.mtime = zipDate(file.lastModified); zip.add(stream);
      const reader = file.stream().getReader();
      try { while (true) { if (signal?.aborted) throw new DOMException('Canceled', 'AbortError'); const next = await reader.read(); if (next.done) break; stream.push(next.value, false); } } finally { reader.releaseLock(); }
      stream.push(new Uint8Array(0), true);
      onProgress({ stage: 'compressing', currentFile: entry.path, processed: index + 1, total: files.length, message: `${index + 1} of ${files.length} files · streaming to OPFS` });
    }
    zip.end();
    if (zipFailure) throw zipFailure;
    if (!finalized) consumePlain(new Uint8Array(0), true);
    const mac = macState.digest(); const digest = digestState.digest();
    encryptedAccess.write(mac, { at: 0 }); encryptedAccess.flush(); encryptedAccess.close();
    const detection = detectionXml(outputName, setupFile, innerSize, key, macKey, iv, mac, digest, msiInfo, useMicrosoftCompatibleMetadata);
    onProgress({ stage: 'assembling', processed: 0, total: encryptedOffset, message: 'Streaming outer container to OPFS' });
    const outputHandle = await root.getFileHandle(outputPath, { create: true }); const outputAccess = await (outputHandle as FileSystemFileHandle & { createSyncAccessHandle: () => Promise<SyncAccess> }).createSyncAccessHandle();
    let outputOffset = 0; const outputDigest = sha256.create();
    const outer = new Zip((error, chunk) => { if (error) throw new Error(String(error)); if (chunk?.length) { const bytes = new Uint8Array(chunk); outputAccess.write(bytes, { at: outputOffset }); outputOffset += bytes.length; outputDigest.update(bytes); } });
    const content = new ZipPassThrough('IntuneWinPackage/Contents/IntunePackage.intunewin'); content.mtime = ZIP_EPOCH; outer.add(content);
    const encryptedFile = await encryptedHandle.getFile(); const reader = encryptedFile.stream().getReader();
    try { while (true) { const next = await reader.read(); if (next.done) break; content.push(next.value, false); } } finally { reader.releaseLock(); }
    content.push(new Uint8Array(0), true);
    const metadata = new ZipPassThrough('IntuneWinPackage/Metadata/Detection.xml'); metadata.mtime = ZIP_EPOCH; outer.add(metadata); metadata.push(detection, true);
    outer.end(); outputAccess.flush(); outputAccess.close();
    onProgress({ stage: 'verifying', processed: 0, total: outputOffset, message: 'Verifying streamed package envelope' });
    const verification = await verifyStreamingPackage(await outputHandle.getFile(), setupFile);
    if (!verification.ok) throw new Error(verification.error);
    await root.removeEntry(encryptedName);
    onProgress({ stage: 'verifying', processed: outputOffset, total: outputOffset, message: 'OPFS package stream verified' });
    return { fileName: outputName, opfsPath: outputPath, byteLength: outputOffset, inputBytes: files.reduce((sum, file) => sum + file.size, 0), fileCount: files.length, durationMs: 0, digest: b64(outputDigest.digest()), warnings: [] };
  } catch (error) {
    try { encryptedAccess.close(); } catch { /* best effort */ }
    try { await root.removeEntry(encryptedName); } catch { /* best effort */ }
    try { await root.removeEntry(outputPath); } catch { /* best effort */ }
    throw error;
  }
}
